import axiosNest from '../../api/axiosNest';

/**
 * Centrum ról i uprawnień — odpowiada `/role-center` w API (decyzja
 * użytkownika 22.10.2026, przełącznik `roleCenter`, S-22.10-01; reguły:
 * `apps/api/src/role-center/`). Wszystko poza `assignable` — wyłącznie
 * administrator; API odpowiada 403 każdemu innemu.
 */

export type PermissionGroup = { key: string; name: string; icon: string; description: string };
export type PermissionEntry = {
  key: string;
  group: string;
  name: string;
  description: string;
  icon: string;
};
export type ElementArea = { key: string; name: string; icon: string };
export type UiElement = {
  key: string;
  area: string;
  kind: 'menu' | 'screen' | 'section' | 'tile';
  name: string;
  description: string;
  icon: string;
  anyOf: string[];
};

export type Catalog = {
  permissionGroups: PermissionGroup[];
  permissions: PermissionEntry[];
  areas: ElementArea[];
  kinds: Record<string, string>;
  elements: UiElement[];
  icons: string[];
  rules: { admin: string; apiClient: string; guestChangeData: string; hiddenNote: string };
};

export type PermissionCell = {
  key: string;
  granted: boolean;
  locked: boolean;
  lockReason: string | null;
};

export type ElementState = {
  key: string;
  visible: boolean;
  permitted: boolean;
  hiddenExplicitly: boolean;
  locked: boolean;
  lockReason: string | null;
};

export type RoleView = {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  system: boolean;
  active: boolean;
  locked: boolean;
  lockReason: string | null;
  userCount: number;
  permissions: string[];
  permissionCells: PermissionCell[];
  elements: ElementState[];
};

export type RoleUser = {
  id: number;
  username: string;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
};

export type RoleDetail = RoleView & { users: RoleUser[] };

export type RoleChange = {
  id: number;
  roleId: string;
  roleName: string;
  kind: 'permission' | 'visibility' | 'role' | string;
  subject: string;
  subjectName: string;
  oldValue: string | null;
  newValue: string | null;
  reason: string;
  changedByName: string | null;
  changedAt: string;
};

export type AccessChanges = {
  permissions?: Record<string, boolean>;
  visibility?: Record<string, boolean>;
};

export type AssignableRole = {
  id: string;
  name: string;
  system: boolean;
  icon: string;
  description: string | null;
  active: boolean;
};

/** Najkrótszy i najdłuższy powód — te same co w API (`role-access.ts`). */
export const MIN_REASON_LENGTH = 10;
export const MAX_REASON_LENGTH = 500;

export async function fetchCatalog(): Promise<Catalog> {
  return (await axiosNest.get('/role-center/catalog')).data as Catalog;
}

export async function fetchRoles(): Promise<RoleView[]> {
  return (await axiosNest.get('/role-center/roles')).data as RoleView[];
}

export async function fetchRole(id: string): Promise<RoleDetail> {
  return (await axiosNest.get(`/role-center/roles/${encodeURIComponent(id)}`)).data as RoleDetail;
}

export async function fetchHistory(roleId?: string): Promise<RoleChange[]> {
  return (await axiosNest.get('/role-center/history', { params: roleId ? { roleId } : {} }))
    .data as RoleChange[];
}

export async function saveAccess(
  id: string,
  changes: AccessChanges,
  reason: string,
): Promise<RoleDetail> {
  return (
    await axiosNest.put(`/role-center/roles/${encodeURIComponent(id)}/access`, {
      ...changes,
      reason,
    })
  ).data as RoleDetail;
}

export async function createRole(input: {
  name: string;
  description?: string | null;
  icon?: string;
  copyFrom?: string | null;
  reason: string;
}): Promise<RoleDetail> {
  return (await axiosNest.post('/role-center/roles', input)).data as RoleDetail;
}

export async function updateRole(
  id: string,
  input: { name?: string; description?: string | null; icon?: string; active?: boolean; reason: string },
): Promise<RoleDetail> {
  return (await axiosNest.patch(`/role-center/roles/${encodeURIComponent(id)}`, input))
    .data as RoleDetail;
}

export async function deleteRole(id: string, reason: string): Promise<void> {
  await axiosNest.delete(`/role-center/roles/${encodeURIComponent(id)}`, { data: { reason } });
}

export async function fetchAssignable(): Promise<AssignableRole[]> {
  return (await axiosNest.get('/role-center/assignable')).data as AssignableRole[];
}

/** Powód zmiany: od 10 do 500 znaków po przycięciu (jak API). */
export function reasonError(reason: string): string | null {
  const r = reason.trim();
  if (r.length < MIN_REASON_LENGTH) {
    return `Podaj powód — co najmniej ${MIN_REASON_LENGTH} znaków (trafi do historii roli i dziennika działań)`;
  }
  if (r.length > MAX_REASON_LENGTH) return `Powód: najwyżej ${MAX_REASON_LENGTH} znaków`;
  return null;
}
